import PropTypes from "prop-types";
import React from "react";
import BootstrapTable from "react-bootstrap/Table";
import { useIntl } from "react-intl";
import * as uuid from "uuid";
import { CHECKBOX, DROPDOWN, PREDEFINED_LIST, RADIO } from "../../../../../ducks/collapsibleTableChart";
import { getAllRowIdentifiersOfStaticTable } from "../../../../../utils/TableUtils";
import {
  formatDecimal,
  formatNumber,
  formatNumberWithPercentage,
  formatPercentage
} from "../NumberFormatUtils";

const NumberQuestion = ({
  table,
  percentage,
  detailsShown,
  staticTableCols,
  firstColumnTitle,
  tableId,
  renderSimpleTable,
  renderStats
}) => {
  const intl = useIntl();
  const isChoiceQuestion = [RADIO, CHECKBOX, DROPDOWN, PREDEFINED_LIST].includes(table.questionType);

  let node = {};
  if (table.anwser) {
    node = table.anwser;
  } else if (table.data) {
    node = table.data[0];
  }

  let nodeData = {};
  let average;
  let title;
  if (staticTableCols) {
    // sum up all elements for a static table
    nodeData.yaxisValue = 0;
    node.answerCount = 0;
    node.yaxisDesc = table[0].yaxisDesc;
    title = table[0].title ? table[0].title : table[0].yaxisDesc;
    table.forEach((answer) => {
      nodeData.yaxisValue += answer.data[0].yaxisValue;
      node.answerCount += answer.answerCount;
    });
    average = nodeData.yaxisValue / node.answerCount;
  } else {
    nodeData = table.anwser ? table.anwser.data[0] : table.data[0];
    average = node.answerCount ? nodeData.yaxisValue / node.answerCount : nodeData.yaxisValue / table.answerCount;
    title = table.title ? table.title : node.yaxisDesc;
  }

  // numbers - expanded zaxisInfo
  const renderNumberExpanded = (title, percentage, sum, subQuestion, i) => {
    const average =
      subQuestion.answerCount && subQuestion.yaxisValue ? subQuestion.yaxisValue / subQuestion.answerCount : 0;
    const number = subQuestion.yaxisValue ? subQuestion.yaxisValue : "-";
    const numberFieldValue =
      percentage && sum ? (isNaN(number) ? "0 %" : formatPercentage((number / sum) * 100, 2)) : formatNumber(number);
    return (
      <tr>
        <td title={subQuestion.zaxisInfo}>{subQuestion.zaxisInfo || subQuestion.zaxisValue || i}</td>
        <td>{numberFieldValue}</td>
        <td>{formatDecimal(average)}</td>
      </tr>
    );
  };

  const renderNumberCollapsedTable = (tableRow, sum, percentage) => {
    let columns = [
      <td key={uuid.v4()}>{`${intl.formatMessage({ id: "collapsible.table.question.total" })}: ${formatNumber(sum)} ${
        percentage ? "(100%)" : ""
      }`}</td>
    ];
    tableRow.forEach((col) => {
      if (col.data) {
        columns.push(
          <td key={uuid.v4()}>
            {col.data[0]?.yaxisValue
              ? percentage
                ? formatPercentage((col.data[0].yaxisValue / sum) * 100, 1)
                : formatNumber(col.data[0].yaxisValue)
              : "-"}
          </td>
        );
      } else {
        columns.push(
          <td key={uuid.v4()}>
            {col.yaxisValue
              ? percentage
                ? formatPercentage((col.yaxisValue / sum) * 100, 1)
                : formatNumber(col.yaxisValue)
              : "-"}
          </td>
        );
      }
    });
    return <tr key={uuid.v4()}>{columns}</tr>;
  };

  const renderSumRowPercentage = (tableRow, sum) => {
    let columns = [
      <td key={uuid.v4()}>{`${intl.formatMessage({ id: "collapsible.table.question.total" })}: ${formatNumber(sum)} ${
        percentage ? "(100%)" : ""
      }`}</td>
    ];
    tableRow.forEach((col) => {
      if (col.data) {
        columns.push(
          <td key={uuid.v4()}>{col.data[0]?.yaxisValue ? formatNumberWithPercentage(col.data[0].yaxisValue) : "-"}</td>
        );
      } else {
        columns.push(<td key={uuid.v4()}>{col.yaxisValue ? formatNumberWithPercentage(col.yaxisValue) : "-"}</td>);
      }
    });
    return <tr key={uuid.v4()}>{columns}</tr>;
  };

  // numbers - expanded zaxisInfo for static tables
  const renderNumberExpandedStaticTable = (title, percentage, sum, subQuestion, table) => {
    const rowIdentifiers = getAllRowIdentifiersOfStaticTable(subQuestion);

    let rows = rowIdentifiers.map((id, i) => (
      <tr key={i}>
        <td>{subQuestion.flat().find((obj) => obj?.zaxisValue === id)?.zaxisInfo || id}</td>
        {subQuestion.map((col, j) => {
          const myCol = col.filter((obj) => obj && obj.zaxisValue === id);
          const colSum = col.reduce(function (total, num) {
            return total + (num.yaxisValue ? num.yaxisValue : 0);
          }, 0);
          const number = myCol.length > 0 ? myCol[0].yaxisValue : "-";
          const numberFieldValue =
            percentage && colSum
              ? isNaN(number)
                ? "0 %"
                : formatPercentage((number / colSum) * 100, 2)
              : formatNumber(number);
          return <td key={j}>{numberFieldValue}</td>;
        })}
      </tr>
    ));

    if (table) {
      percentage
        ? rows.push(renderSumRowPercentage(table, sum ? sum : "-"))
        : rows.push(renderNumberCollapsedTable(table, sum ? sum : "-", percentage));
    }

    return rows;
  };

  return (
    <BootstrapTable responsive striped bordered hover className="horizontal" id={`table_${tableId}`}>
      <thead className="bg-secondary text-light">
        {Array.isArray(table) && renderSimpleTable ? (
          <tr key={uuid.v4()}>
            <th key={uuid.v4()}>{firstColumnTitle}</th>
            {table.map((col) => (
              <th key={uuid.v4()}>{col.title ? col.title : "-"}</th>
            ))}
          </tr>
        ) : (
          <>
            {isChoiceQuestion ? (
              <tr>
                <th>{firstColumnTitle}</th>
                {table.anwser?.data?.map((col, i) => (
                  <th key={i}>{col.xaxisInfo || col.xaxisValue || "-"}</th>
                ))}
              </tr>
            ) : (
              <tr>
                <th>{firstColumnTitle}</th>
                <th>{title ? title : "-"}</th>
                <th></th>
              </tr>
            )}
          </>
        )}
      </thead>
      <tbody>
        {detailsShown
          ? renderSimpleTable
            ? renderNumberExpandedStaticTable(title, percentage, nodeData.yaxisValue, detailsShown, table)
            : detailsShown.map(renderNumberExpanded.bind(this, title, percentage, nodeData.yaxisValue))
          : Array.isArray(table) && renderSimpleTable
          ? renderNumberCollapsedTable(table, nodeData.yaxisValue ? nodeData.yaxisValue : "-", percentage)
          : isChoiceQuestion
          ? renderNumberCollapsedTable(table.anwser?.data, nodeData.yaxisValue ? nodeData.yaxisValue : "-", percentage)
          : null}

        {renderStats && (
          <tr className="bg-light">
            <td>{intl.formatMessage({ id: "collapsible.table.question.total" })}</td>
            <td>
              ∑ {nodeData.yaxisValue ? formatNumber(nodeData.yaxisValue) : "-"} {percentage ? "(100%)" : ""}
            </td>
            <td>Ø {average ? formatDecimal(average) : "-"}</td>
          </tr>
        )}
      </tbody>
    </BootstrapTable>
  );
};

NumberQuestion.propTypes = {
  table: PropTypes.object,
  percentage: PropTypes.bool,
  intl: PropTypes.object,
  detailsShown: PropTypes.object,
  staticTableCols: PropTypes.bool,
  firstColumnTitle: PropTypes.object,
  tableId: PropTypes.string,
  renderSimpleTable: PropTypes.bool,
  renderStats: PropTypes.bool
};

export default NumberQuestion;
